"use client";

import { CTA } from "@/app/(public)/shared/components/CTA";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import { sendSlackMessageNoAuth } from "@/components/ui/utils";
import { getCurrentUser } from "@/lib/db/users";
import { SlackNotificationType } from "@/lib/shared/enums";
import { createClient } from "@/utils/supabase/client";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

// Activation image paths
const espressoActivation1 = "/landing/activations/selfielatte-activation.jpeg";
const espressoActivation2 = "/landing/activations/selfielatte-activation-2.JPG";
const espressoActivation3 = "/landing/activations/selfielatte-activation-3.JPG";
const puppyYogaActivation1 = "/landing/activations/puppyyoga-activation.png";
const puppyYogaActivation2 = "/landing/activations/puppyyoga-activation-2.jpg";
const puppyYogaActivation3 = "/landing/activations/puppyyoga-activation-3.jpg";
const officePartyActivation1 = "/landing/activations/officeparty-activation.jpg";
const officePartyActivation2 = "/landing/activations/officeparty-activation-2.jpg";
const officePartyActivation3 = "/landing/activations/officeparty-activation-3.jpeg";
const headshotsActivation1 = "/landing/activations/headshots-activation.jpg";
const headshotsActivation2 = "/landing/activations/headshots-activation-2.jpg";
const headshotsActivation3 = "/landing/activations/headshots-activation-3.jpg";
const tahoeActivation1 = "/landing/activations/tahoe-activation.JPG";
const tahoeActivation2 = "/landing/activations/tahoe-activation-2.png";
const tahoeActivation3 = "/landing/activations/tahoe-activation-3.JPG";
const localRetreatActivation1 = "/landing/activations/localretreat-activation.jpg";
const localRetreatActivation2 = "/landing/activations/localretreat-activation-2.jpg";
const localRetreatActivation3 = "/landing/activations/localretreat-activation-3.jpg";
const novelaActivation1 = "/landing/activations/novela-activation.jpg";
const novelaActivation2 = "/landing/activations/novela-activation-2.jpg";
const novelaActivation3 = "/landing/activations/novela-activation-3.jpg";
const internsActivation = "/landing/activations/fourinterns-activation.JPG";
const merchActivation = "/landing/activations/merch-activation.PNG";
const merchActivation2 = "/landing/activations/merch-activation-2.JPG";
const merchActivation3 = "/landing/activations/merch-activation-3.PNG";
const coverImage = "/landing/activations/activation-hero.jpg";

interface Activation {
  id: string;
  title: string;
  shortDescription: string;
  features: string[];
  detailedDescription: string;
  duration?: string;
  groupSize?: string;
  includes?: string[];
  idealFor?: string;
  setup?: string;
  additionalInfo?: string;
  pricing?: string;
  images?: any[];
  coverImage?: string;
  testimonials?: { name: string; role: string; quote: string }[];
}

const activations: Activation[] = [
  {
    id: "espresso-cart",
    title: "Custom Latte Art Cart",
    shortDescription:
      "Personalize your coffee experience with custom printed latte art featuring photos, logos, and designs",
    features: [
      "Custom photo/logo printing on lattes",
      "Professional barista service",
      "Premium coffee selection",
      "High-quality latte art printer",
    ],
    detailedDescription:
      "Make your coffee breaks unforgettable with our custom latte art printing service. Our specialized printer can create stunning, edible designs on your lattes - from company logos and team photos to personal pictures and custom artwork. Our professional baristas combine premium coffee with this unique personalization for an Instagram-worthy experience your team will love.",
    duration: "2 days",
    groupSize: "20-200 people",
    pricing: "$5,000 for 2-day service",
    includes: [
      "Professional barista team",
      "Mobile espresso cart with latte art printer",
      "Premium coffee beans and milk alternatives",
      "Custom photo/logo printing on drinks",
      "Digital photo collection service",
      "Setup and breakdown",
      "High-resolution photo documentation",
    ],
    idealFor:
      "Team celebrations, client meetings, product launches, brand activations, social media campaigns, and employee appreciation events",
    setup:
      "Requires minimal space (6x6 ft) with access to power outlet. Can be set up in lobby, cafeteria, or outdoor spaces.",
    additionalInfo:
      "Submit your custom designs on the spot for immediate printing. Available for recurring bookings at discounted rates. Perfect for creating shareable social media moments - employees love posting their personalized lattes!",
    images: [espressoActivation1, espressoActivation2, espressoActivation3],
    coverImage: espressoActivation1,
  },
  {
    id: "puppy-yoga",
    title: "Puppy Yoga",
    shortDescription: "Combine wellness and joy with an unforgettable puppy yoga session at your office",
    features: [
      "Certified yoga instructor",
      "Adorable therapy puppies",
      "All yoga equipment provided",
      "Stress relief & team bonding",
    ],
    detailedDescription:
      "Experience the ultimate stress-relief activity with our puppy yoga sessions. Professional yoga instructors guide your team through a gentle flow while adorable, trained puppies roam freely, providing cuddles and comic relief throughout the practice. More intimate sessions with 22 people per 75-minute class.",
    duration: "75 minutes per class (1-3 consecutive classes available)",
    groupSize: "22 people per class",
    pricing: "$3,000-$5,000 depending on number of classes",
    includes: [
      "Certified yoga instructor",
      "6-10 trained therapy puppies with handlers",
      "Yoga mats and props",
      "Sanitization supplies",
      "Photo opportunities",
      "Puppy playtime after class",
      "Optional: Wellness shots/smoothies for post-yoga networking reception",
    ],
    idealFor:
      "Team building events, wellness initiatives, stress relief during busy periods, employee appreciation days, or Friday afternoon activities with networking",
    setup:
      "Requires open space (conference room, cafeteria, or outdoor area). We handle all setup including protective floor covering.",
    additionalInfo:
      "All puppies are health-certified and come from reputable local shelters or therapy programs. Sessions can be adapted for all fitness levels. Add-on wellness shots and smoothies available for a networking reception after the yoga session.",
    images: [puppyYogaActivation1, puppyYogaActivation2, puppyYogaActivation3],
    coverImage: puppyYogaActivation3,
  },
  {
    id: "office-warming",
    title: "Office Warming Party",
    shortDescription: "Celebrate your new space or refresh your current one with a memorable office party",
    features: ["Full event planning", "Catering & beverages", "Entertainment options", "Decor & ambiance"],
    detailedDescription:
      "Whether you're moving to a new space or want to celebrate a milestone, our office warming parties create memorable experiences. We handle everything from catering and decorations to entertainment and activities, ensuring your team and guests have an amazing time.",
    duration: "3-5 hours",
    groupSize: "30-500 people",
    pricing: "$5,000 per rental",
    includes: [
      "Event planning and coordination",
      "Caricature artist",
      "Bi-Rite catering with multiple menu options",
      "Bar service (alcoholic/non-alcoholic)",
      "DJ or live music options",
      "Decorations and lighting",
      "Photo booth with props",
      "Games and activities",
      "Setup and cleanup crew",
    ],
    idealFor:
      "New office launches, company anniversaries, merger celebrations, holiday parties, recruiting events,or significant milestone celebrations",
    setup:
      "We work with your office layout to create optimal flow. Can utilize multiple spaces including rooftops, lobbies, and outdoor areas.",
    additionalInfo:
      "Fully customizable themes available. We handle all vendor coordination and can work within various budget ranges. Check out photos from our recent office warming events!",
    images: [officePartyActivation1, officePartyActivation2, officePartyActivation3],
    coverImage: officePartyActivation1,
  },
  {
    id: "headshots",
    title: "Professional Headshots",
    shortDescription: "Update your team's professional photos with an on-site headshot session",
    features: ["Professional photographer", "On-site studio setup", "3-day delivery", "Multiple backgrounds"],
    detailedDescription:
      "Give your team polished, professional headshots without leaving the office. Our photographers bring a complete mobile studio setup, ensuring consistent, high-quality portraits for your entire team's LinkedIn profiles, company website, and marketing materials.",
    duration: "4 hours of shooting, 1 hour prep & wrap",
    groupSize: "10-200 people",
    pricing: "~$2,000-$3,000",
    includes: [
      "Professional photographer and assistant",
      "Complete mobile studio setup",
      "Multiple backdrop options",
      "Professional lighting equipment",
      "Real-time preview screen",
      "Basic retouching included",
      "High-resolution digital files delivered in 3 business days",
      "Online gallery for selection",
    ],
    idealFor:
      "Team directory updates, new hire onboarding, website refreshes, LinkedIn profile updates, or annual photo updates",
    setup:
      "Requires 10x10 ft space with standard power outlets. We can set up in conference rooms, lobbies, or designated areas.",
    additionalInfo:
      "5-7 minute sessions per person. Employees receive 3-5 retouched photos within 3 business days. Group shots and executive portraits available. See examples from our team's recent headshot session!",
    images: [headshotsActivation1, headshotsActivation2, headshotsActivation3],
    coverImage: headshotsActivation1,
  },
  {
    id: "local-retreat",
    title: "Local Team Retreat",
    shortDescription: "Escape the office for a rejuvenating 2-night local retreat with your team",
    features: ["19 private rooms", "2-night stay", "Flexible sleeping arrangements", "Spa-inspired amenities"],
    detailedDescription:
      "Perfect for teams looking to bond and strategize away from the office. Our local retreat package includes 19 rooms with options for additional guests to share accommodations. Features unique lodging options including a private Queen Bedroom with closing door, pull-out futon in living areas, and Adventure Tents with king beds and convertible sofas.",
    duration: "2 nights",
    groupSize: "20-25 people (with room sharing options)",
    pricing: "$20,000 for complete package",
    includes: [
      "19 private rooms for 2 nights",
      "Private Queen Bedroom with closing door",
      "Pull-out full size futon in living area",
      "Adventure Tent with king bed and seating area",
      "Convertible sofa (folds into double bed)",
      "Access to spa-inspired bathrooms in Clubhouse",
      "Private Airstream bathroom option",
      "Common areas for team activities",
      "Meeting spaces for workshops",
    ],
    idealFor:
      "Strategic planning sessions, team building retreats, quarterly offsites, leadership workshops, or celebration getaways",
    setup:
      "The Adventure Tent includes both sleeping and seating areas. A king bed and seating area comes complete with convertible sofa that folds out into a double bed – perfect for one adult or two small children. Guests enjoy spa-inspired bathrooms and showers in the Clubhouse, or use the private Airstream bathroom.",
    additionalInfo:
      "Room sharing available for those comfortable with it - the convertible sofas and pull-out futtons allow for flexible sleeping arrangements to accommodate 20-25 people total. Perfect for teams who want a local getaway without extensive travel.",
    images: [localRetreatActivation1, localRetreatActivation2, localRetreatActivation3],
    coverImage: localRetreatActivation1,
  },
  {
    id: "novela-mixer",
    title: "Private Bar Mixer at Novela",
    shortDescription: "Host an exclusive networking event at San Francisco's premier literary-themed cocktail bar",
    features: ["Private venue buyout", "Craft cocktails & appetizers", "Up to 300 guests", "Prime SF location"],
    detailedDescription:
      "Take your team networking to the next level with a private event at Novela, San Francisco's beloved literary-themed cocktail bar. This sophisticated venue offers the perfect atmosphere for client entertainment, team celebrations, or networking events. With its unique ambiance, craft cocktails, and central location, Novela provides an unforgettable experience.",
    duration: "3-5 hours",
    groupSize: "50-300 people",
    pricing: "$10,000-$40,000 depending on guest count and package",
    includes: [
      "Private venue buyout or semi-private space",
      "Dedicated bartenders and service staff",
      "Custom cocktail menu options",
      "Premium open bar packages",
      "Passed appetizers and food stations",
      "AV equipment for presentations",
      "Event coordination and planning",
      "Coat check service",
      "Valet parking coordination available",
    ],
    idealFor:
      "Client entertainment, holiday parties, product launches, milestone celebrations, networking events, or team achievement celebrations",
    setup:
      "Located in the heart of San Francisco's SoMa district. Multiple spaces available including the main bar, library room, and outdoor patio. Can accommodate various event formats from cocktail receptions to seated dinners.",
    additionalInfo:
      "Novela's literary theme creates unique conversation starters with book-inspired cocktails and decor. Perfect for companies wanting a sophisticated yet relaxed atmosphere. Custom branding and menu personalization available.",
    images: [novelaActivation1, novelaActivation2, novelaActivation3],
    coverImage: novelaActivation1,
  },
  {
    id: "tahoe-retreat",
    title: "Tahoe Coworking Offsite",
    shortDescription: "Combine work and play with a productive retreat in stunning Lake Tahoe",
    features: [
      "Luxury Tahoe Airbnb",
      "Work & recreation balance",
      "Mountain & lake activities",
      "Full trip coordination",
    ],
    detailedDescription:
      "Escape to Lake Tahoe for the ultimate coworking offsite that perfectly balances productivity with recreation. Our curated Tahoe retreats feature stunning lakefront or mountain properties with dedicated workspace, high-speed internet, and access to year-round outdoor activities. Whether it's summer hiking and water sports or winter skiing and snowboarding, your team will return refreshed and inspired.",
    duration: "3-5 days",
    groupSize: "10-30 people",
    pricing: "Starting at $25,000 for 3-day package",
    includes: [
      "Luxury Airbnb rental with workspace",
      "High-speed internet and AV setup",
      "Transportation from Bay Area",
      "Welcome dinner at lakeside restaurant",
      "Daily breakfast and lunch catering",
      "Activity coordination (skiing, hiking, water sports)",
      "Team building facilitator available",
      "Evening entertainment options",
      "All linens and amenities",
      "Pre-arrival grocery stocking",
    ],
    idealFor:
      "Strategic planning sessions, quarterly kickoffs, team bonding retreats, hackathons, creative workshops, or leadership offsites",
    setup:
      "Properties feature dedicated workspaces, multiple gathering areas, full kitchens, hot tubs, and outdoor spaces. Located 3-4 hours from San Francisco with options in North or South Lake Tahoe.",
    additionalInfo:
      "Seasonal activities include skiing at Northstar or Heavenly, summer lake activities, hiking trails, and mountain biking. Properties range from modern luxury homes to classic Tahoe cabins. Custom itineraries available to balance work sessions with recreation.",
    images: [tahoeActivation1, tahoeActivation2, tahoeActivation3],
    coverImage: tahoeActivation1,
  },
  {
    id: "free-interns",
    title: "Four Interns for a Day",
    shortDescription: "Give your team a day of interns to help with your work",
    features: ["4 hard working interns", "Resident Pawrty Planner", "No training required"],
    detailedDescription:
      "Give your team a day of free interns to help with your work. We will provide four interns for a day of work. They will be trained on your specific needs and will be able to help with your work.",
    duration: "1 day",
    groupSize: "any number of people",
    pricing: "Priceless",
    includes: ["CEO", "COO", "Founding Engineer", "Founding Product Engineer"],
    images: [internsActivation],
    coverImage: internsActivation,
  },
  {
    id: "merch-design",
    title: "Merch Design",
    shortDescription: "Design your own merch with our team",
    features: ["Professional designer", "3-day delivery", "Multiple backgrounds"],
    detailedDescription:
      "Design your own merch with our team. We will provide a professional designer to help you design your own merch. They will be able to help you with your design and will be able to help you with your design. Items include light cubes, sunglasses, and more.",
    duration: "1 day",
    groupSize: "any number of people",
    pricing: "~$1,000",
    includes: ["Light cubes", "Sunglasses", "T-shirts", "Hats", "Mugs", "Stickers", "Posters", "Banners", "Candles"],
    images: [merchActivation, merchActivation2, merchActivation3],
    coverImage: merchActivation,
  },
];

export default function Activations() {
  const { openModal, closeModal, isModalOpen } = useModalManager();
  const [selectedActivation, setSelectedActivation] = useState<Activation | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isBookingFormOpen, setIsBookingFormOpen] = useState(false);
  const [bookingFormData, setBookingFormData] = useState({
    name: "",
    email: "",
    eventDate: "",
    notes: "",
    activationTitle: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      const supabase = createClient();
      const user = await getCurrentUser(supabase);

      if (user) {
        setBookingFormData((prev) => ({
          ...prev,
          name: user.full_name || "",
          email: user.email || "",
        }));
      }
    };

    fetchUserData();
  }, []);

  const handleActivationClick = (activation: Activation) => {
    setSelectedActivation(activation);
    setCurrentImageIndex(0);
    openModal(activation.id);
  };

  const handleBookActivation = async (activationTitle: string) => {
    // Fetch latest user data when opening form
    const supabase = createClient();
    const user = await getCurrentUser(supabase);

    setBookingFormData((prev) => ({
      ...prev,
      activationTitle,
      name: user?.full_name || prev.name || "",
      email: user?.email || prev.email || "",
    }));
    setIsBookingFormOpen(true);
  };

  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!bookingFormData.name.trim() || !bookingFormData.email.trim() || !bookingFormData.eventDate) {
      alert("Please fill in all required fields.");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(bookingFormData.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/activations/book", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingFormData),
      });

      if (response.ok) {
        // Close all modals
        setIsBookingFormOpen(false);
        if (selectedActivation) {
          closeModal(selectedActivation.id);
        }
        // Reset form but keep user data
        const supabase = createClient();
        const user = await getCurrentUser(supabase);
        setBookingFormData({
          name: user?.full_name || "",
          email: user?.email || "",
          eventDate: "",
          notes: "",
          activationTitle: "",
        });
        alert("Thank you for your booking request! We'll get back to you within a few days.");
      } else {
        alert("There was an error submitting your booking. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting booking:", error);
      alert("There was an error submitting your booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }

    // Slack notification
    await sendSlackMessageNoAuth(
      SlackNotificationType.WEBSITE,
      "ACTIVATION BOOKING",
      `${bookingFormData.name} (${bookingFormData.email}) submitted a booking request for ${bookingFormData.activationTitle}`,
      [
        { title: "Name", message: bookingFormData.name },
        { title: "Email", message: bookingFormData.email },
        { title: "Activation", message: bookingFormData.activationTitle },
      ]
    );
  };

  return (
    <div className="mt-[64px]">
      {/* Hero Section */}
      <section className="min-h-[500px] lg:h-[calc(100vh-64px)] py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 h-full items-center">
            <motion.div
              className="flex flex-col justify-center gap-8"
              initial={{ opacity: 0, x: -32 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex flex-col gap-6">
                <h1 className="text-h1 text-landing-primary">Employee Engagement Activations</h1>
                <p className="text-body-lg text-landing-secondary max-w-xl">
                  Boost morale, celebrate milestones, and create unforgettable experiences with our curated activation
                  services. From daily perks to special celebrations, we handle all the details so you can focus on what
                  matters most - your people.
                </p>
              </div>
              <div>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => {
                    const activationsSection = document.getElementById("activations-section");
                    activationsSection?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Explore Activations
                </Button>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center justify-center order-first lg:order-last"
              initial={{ opacity: 0, x: 32 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="relative w-full max-w-md lg:max-w-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-landing-cta-light/10 to-landing-cta-dark/10 rounded-2xl transform rotate-3"></div>
                <img
                  src="/landing/activations/activation-hero.JPG"
                  alt="Employee engagement activations"
                  className="relative w-full h-auto max-h-[400px] lg:max-h-[500px] rounded-2xl shadow-2xl object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Activations Grid */}
      <section className="py-12 sm:py-16 md:py-20" id="activations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-h2 text-landing-primary">Explore Activations</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {activations.map((activation, index) => (
              <motion.div
                key={activation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => handleActivationClick(activation)}
              >
                <div className="h-full rounded-xl border-2 border-violet-100 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col overflow-hidden">
                  {/* Cover Image */}
                  {activation.coverImage && (
                    <div className="relative h-48 w-full">
                      <img
                        src={activation.coverImage}
                        alt={activation.title}
                        className={`w-full h-full object-cover ${
                          activation.id === "espresso-cart"
                            ? "object-[center_30%]"
                            : activation.id === "local-retreat"
                              ? "object-[center_60%]"
                              : activation.id === "tahoe-retreat"
                                ? "object-[center_90%]"
                                : ""
                        }`}
                      />
                    </div>
                  )}

                  {/* Card Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    {/* Content */}
                    <h3 className="text-h4 text-landing-primary mb-3">{activation.title}</h3>
                    <p className="text-body text-landing-secondary mb-4 flex-grow">{activation.shortDescription}</p>

                    {/* Features */}
                    <ul className="space-y-2 mb-6">
                      {activation.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="text-violet-600 text-lg leading-none">•</span>
                          <span className="text-body-sm text-landing-secondary">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <Button variant="outline" size="sm" className="w-full">
                      Learn More
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-h3 text-landing-primary mb-8">Why Choose Our Activations?</h2>
          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center">
                <span className="text-violet-600 font-bold">1</span>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary mb-2">Turnkey Solutions</h4>
                <p className="text-body text-landing-secondary">
                  We handle everything from planning to execution, so you can focus on enjoying the experience with your
                  team.
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center">
                <span className="text-violet-600 font-bold">2</span>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary mb-2">Flexible Scheduling</h4>
                <p className="text-body text-landing-secondary">
                  Book one-time events or set up recurring activations. We work around your schedule and office
                  requirements.
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center">
                <span className="text-violet-600 font-bold">3</span>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary mb-2">Measurable Impact</h4>
                <p className="text-body text-landing-secondary">
                  Track engagement, gather feedback, and see the positive impact on team morale and productivity.
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center">
                <span className="text-violet-600 font-bold">4</span>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary mb-2">Budget-Friendly Options</h4>
                <p className="text-body text-landing-secondary">
                  From simple coffee carts to elaborate celebrations, we have options for every budget and team size.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Ready to boost team engagement?"
        description="Let us help you create memorable experiences that your team will love. From quick coffee breaks to unforgettable trips, we've got you covered."
        primaryButtonText="Book an Activation"
      />

      {/* Activation Detail Modals */}
      {activations.map((activation) => (
        <UnifiedModal
          key={activation.id}
          type="content"
          isOpen={isModalOpen(activation.id)}
          onClose={() => closeModal(activation.id)}
          title={activation.title}
          subtitle="Complete activation details"
          size="md"
          closeOnBackdrop={true}
          closeOnEscape={true}
        >
          <div className="space-y-6">
            {/* Image Carousel */}
            {activation.images && activation.images.length > 0 && (
              <div className="relative">
                <div className="relative aspect-[16/9] w-full overflow-hidden">
                  <img
                    src={activation.images[currentImageIndex]}
                    alt={`${activation.title} image ${currentImageIndex + 1}`}
                    className={`w-full h-full object-cover ${
                      activation.id === "espresso-cart" && currentImageIndex === 0
                        ? "object-[center_10%]"
                        : activation.id === "local-retreat" && currentImageIndex === 0
                          ? "object-[center_55%]"
                          : activation.id === "tahoe-retreat" && currentImageIndex === 2
                            ? "object-[center_70%]"
                            : activation.id === "tahoe-retreat" && currentImageIndex === 1
                              ? "object-[center_85%]"
                              : activation.id === "tahoe-retreat" && currentImageIndex === 0
                                ? "object-[center_85%]"
                                : ""
                    }`}
                  />

                  {/* Navigation arrows if multiple images */}
                  {activation.images.length > 1 && (
                    <>
                      <button
                        onClick={() =>
                          setCurrentImageIndex(
                            (prev) => (prev - 1 + activation.images!.length) % activation.images!.length
                          )
                        }
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex((prev) => (prev + 1) % activation.images!.length)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>

                      {/* Image indicators */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {activation.images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              idx === currentImageIndex ? "bg-white" : "bg-white/50"
                            }`}
                            aria-label={`Go to image ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="px-6 pb-6 space-y-6">
              {/* Overview */}
              <div>
                <p className="text-body-lg text-landing-primary">{activation.detailedDescription}</p>
              </div>

              {/* Key Details */}
              <div className="grid grid-cols-3 gap-4 py-4 border-y border-gray-200 text-center">
                {activation.duration && (
                  <div>
                    <p className="text-body-sm text-landing-secondary mb-1">Duration</p>
                    <p className="text-body font-semibold text-landing-primary">{activation.duration}</p>
                  </div>
                )}
                {activation.groupSize && (
                  <div>
                    <p className="text-body-sm text-landing-secondary mb-1">Group Size</p>
                    <p className="text-body font-semibold text-landing-primary">{activation.groupSize}</p>
                  </div>
                )}
                {activation.pricing && (
                  <div>
                    <p className="text-body-sm text-landing-secondary mb-1">Price</p>
                    <p className="text-body-lg font-bold text-violet-600">{activation.pricing}</p>
                  </div>
                )}
              </div>

              {/* What's Included */}
              {activation.includes && (
                <div className="space-y-3">
                  <h4 className="text-body-lg font-semibold text-landing-primary">What's Included</h4>
                  <ul className="space-y-2">
                    {activation.includes.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="text-violet-600">✓</span>
                        <span className="text-body text-landing-secondary">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Ideal For */}
              {activation.idealFor && (
                <div className="space-y-3">
                  <h4 className="text-body-lg font-semibold text-landing-primary">Ideal For</h4>
                  <p className="text-body text-landing-secondary">{activation.idealFor}</p>
                </div>
              )}

              {/* Setup Requirements */}
              {activation.setup && (
                <div className="space-y-3">
                  <h4 className="text-body-lg font-semibold text-landing-primary">Setup Requirements</h4>
                  <p className="text-body text-landing-secondary">{activation.setup}</p>
                </div>
              )}

              {/* Additional Information */}
              {activation.additionalInfo && (
                <div className="space-y-3 p-4 bg-violet-50 rounded-lg">
                  <h4 className="text-body-lg font-semibold text-landing-primary">Good to Know</h4>
                  <p className="text-body text-landing-secondary">{activation.additionalInfo}</p>
                </div>
              )}

              {/* CTA Button */}
              <div className="pt-4 border-t border-gray-200">
                {activation.id === "free-interns" ? (
                  <Button disabled className="w-full opacity-50 cursor-not-allowed">
                    Not Available
                  </Button>
                ) : (
                  <Button onClick={() => handleBookActivation(activation.title)} className="w-full">
                    Book This Activation
                  </Button>
                )}
              </div>
            </div>
          </div>
        </UnifiedModal>
      ))}

      {/* Booking Form Modal */}
      <UnifiedModal
        type="content"
        isOpen={isBookingFormOpen}
        onClose={() => setIsBookingFormOpen(false)}
        title="Book an Activation"
        subtitle={bookingFormData.activationTitle}
        size="md"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <form onSubmit={handleSubmitBooking} className="p-6 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              required
              value={bookingFormData.name}
              onChange={(e) => setBookingFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              required
              value={bookingFormData.email}
              onChange={(e) => setBookingFormData((prev) => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 mb-1">
              Preferred Event Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="eventDate"
              required
              value={bookingFormData.eventDate}
              onChange={(e) => setBookingFormData((prev) => ({ ...prev, eventDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              min={new Date().toISOString().split("T")[0]}
            />
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              id="notes"
              rows={4}
              value={bookingFormData.notes}
              onChange={(e) => setBookingFormData((prev) => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="Tell us more about your event, number of attendees, special requirements, etc."
            />
          </div>

          <div className="pt-4 flex gap-3">
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                !bookingFormData.name.trim() ||
                !bookingFormData.email.trim() ||
                !bookingFormData.eventDate
              }
              className="flex-1"
            >
              {isSubmitting ? "Submitting..." : "Submit Booking Request"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setIsBookingFormOpen(false)} className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </UnifiedModal>
    </div>
  );
}
